import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Stack,
  Paper,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import default_theme from "assets/themes/themes";
import {
  fetchLapRequest,
  createLapRequest,
  updateLapRequest,
  publishLapRequest,
} from "../../redux/laps/lapSlice";
import { fetchDraftsRequest } from "../../redux/drafts/draftsSlice";
import { fetchPublishedRequest } from "../../redux/published/publishedSolutionsSlice";
import DraftSolutionDetails from "../DraftSolutionDetails/DraftSolutionDetails";

const LocalActionPlanSection = ({ projectId }) => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  
  // Grab creating and updating states to show a loading spinner on the Save button
  const { data: lap, loading, publishing, creating, updating } = useSelector((state) => state.laps);
  const drafts = useSelector((state) => state.drafts.items || []);
  const publishedItems = useSelector((state) => state.publishedSolutions?.items || []);

  const [form, setForm] = useState({ title: "", description: "" });
  const [viewDraft, setViewDraft] = useState(null);
  
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [solutionToRemove, setSolutionToRemove] = useState(null);
  // State for the confirmation popup
  const [openPublishDialog, setOpenPublishDialog] = useState(false);
  const [isPublishingSequence, setIsPublishingSequence] = useState(false);

  useEffect(() => {
  if (projectId) {
    dispatch(fetchLapRequest(projectId));
    dispatch(fetchDraftsRequest(projectId));
    dispatch(fetchPublishedRequest(projectId));
  }
}, [dispatch, projectId]);

  useEffect(() => {
    if (lap) {
      setForm({
        title: lap.title || "",
        description: lap.description || "",
      });
    } else {
      setForm({
        title: "",
        description: "",
      });
    }
  }, [lap]);

  const solutionLookup = useMemo(() => {
    const map = new Map();
    [...publishedItems, ...drafts].forEach((item) => {
      const id = String(item.id || item.entryId);
      if (!map.has(id)) map.set(id, item);
    });
    return map;
  }, [drafts, publishedItems]);

  const attachedSolutions = useMemo(() => {
    const ids = (lap?.solution_ids || []).slice(0, 3);
    return ids
      .map((id) => solutionLookup.get(String(id)))
      .filter(Boolean);
  }, [lap, solutionLookup]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDynamicSave = () => {
    const payload = {
      ...form,
      solution_ids: lap?.solution_ids || [],
    };

    if (lap && lap.id) {
      dispatch(updateLapRequest({ lapId: lap.id, projectId, data: payload }));
    } else {
      dispatch(createLapRequest({ projectId, data: payload }));
    }
  };

  const confirmRemove = (id) => {
      setSolutionToRemove(id);
      setRemoveDialogOpen(true);
  };

  const executeRemove = () => {
    if (!lap?.id || !solutionToRemove) return;
    const nextIds = (lap.solution_ids || []).filter(id => String(id) !== String(solutionToRemove));
    
    dispatch(updateLapRequest({
        lapId: lap.id,
        projectId,
        data: {
            title: lap.title || "",
            description: lap.description || "",
            solution_ids: nextIds,
        },
    }));
    
    setRemoveDialogOpen(false);
    setSolutionToRemove(null);
  };

  const getSafeText = (val) => {
    if (!val) return "";
    if (typeof val === 'string') return val;
    if (val.en) return val.en;
    if (val.english) return val.english;
    return String(val);
  };

  const formatRddTags = (rddString) => {
      if (!rddString) return [];
      return rddString.split(',').map(r => r.trim()).filter(Boolean);
  };

  const handleConfirmPublish = async () => {
    setOpenPublishDialog(false);
    setIsPublishingSequence(true); 
    
    try {
        if (dispatch(publishLapRequest({ projectId, lap })).unwrap) {
            await dispatch(publishLapRequest({ projectId, lap })).unwrap();
        } else {
            await dispatch(publishLapRequest({ projectId, lap }));
        }
        
        const fetchPromises = [
            dispatch(fetchDraftsRequest(projectId)),
            dispatch(fetchPublishedRequest(projectId))
        ];

        await Promise.all(fetchPromises.map(p => p.unwrap ? p.unwrap() : p));
        
    } catch (error) {
        console.error("Error during the publishing sequence:", error);
    } finally {
        setIsPublishingSequence(false); // 4. Stop the spinner. 
    }
  };

  if (loading && !lap) {
    return (
      <Box sx={{ p: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "#fff", borderRadius: 3, border: `1px solid ${default_theme.palette.border.subtleLight}` }}>
        <CircularProgress size={36} sx={{ color: default_theme.palette.other.chatGreen, mb: 2 }} />
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
          Loading Local Action Plan...
        </Typography>
      </Box>
    );
  }

  // Check if there are unsaved changes
  const hasChanges = form.title !== (lap?.title || "") || form.description !== (lap?.description || "");
  const isSaving = creating || updating;

  const isLapPublished = attachedSolutions.length > 0 && attachedSolutions.every(
      (sol) => publishedItems.some((pubItem) => 
          String(pubItem.entryId) === String(sol.entryId || sol.id) || 
          String(pubItem.id) === String(sol.entryId || sol.id)
      )
  );

  // Reusable text field styles 
  const textFieldStyles = {
    backgroundColor: "#fff", 
    borderRadius: 1,
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#23576B",
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderColor: "#23576B",
        borderWidth: "2px",
      }
    }
  };
  // A LAP is considered "published" if it has attached solutions AND 
  // ALL of its attached solutions exist in the `publishedItems` array .
  
  return (
    <Box sx={{ p: "2rem 2.25rem", mb: 4, borderRadius: "20px", backgroundColor: "#ffffff", border: "1px solid rgba(35, 87, 107, 0.12)", boxShadow: "0 2px 12px rgba(35, 87, 107, 0.06)", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: 0, width: "100%", height: "4px", background: "linear-gradient(90deg, #13a89e, #0fe880)" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", mb: "1.5rem" }}>
        <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", background: "#35B3AB", flexShrink: 0 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase" }}>
          Local Action Plan Overview
        </Typography>
      </Box>

      <Typography variant="body2" sx={{ color: "#7898A4", mb: 4 }}>
        Outline the LAP title and description, then attach up to three draft solutions. 
      </Typography>

      <Stack spacing={3} sx={{ mb: 2 }}>
        <TextField
          fullWidth
          label="LAP Title"
          name="title"
          value={form.title}
          onChange={handleChange}
          size="small"
          sx={textFieldStyles}
          disabled={isLapPublished}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          name="description"
          value={form.description}
          onChange={handleChange}
          sx={textFieldStyles}
          disabled={isLapPublished}
        />
      </Stack>

      {/* Save Button - Hidden if Published */}
      {!isLapPublished && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
          <Button
            variant="contained"
            onClick={handleDynamicSave}
            disabled={!hasChanges || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: 2, backgroundColor: "#23576B", color: "#fff",
              "&:hover": { backgroundColor: "#1B4454" }, "&.Mui-disabled": { backgroundColor: "#E4E7EC", color: "#98A2B3" }
            }}
          >
            {isSaving ? "Saving..." : "Save LAP Details"}
          </Button>
        </Box>
      )}

      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#23576B" }}>
          Attached Solutions
        </Typography>
        <Chip 
          label={`${attachedSolutions.length} / 3`} 
          size="small" 
          sx={{ backgroundColor: attachedSolutions.length === 3 ? "#E8F5E9" : "#F1F5F9", color: attachedSolutions.length === 3 ? "#2E7D32" : "#475467", fontWeight: 600 }}
        />
      </Box>

      {attachedSolutions.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", p: 3, textAlign: "center", border: "1px dashed #ccc", borderRadius: 2, mb: 4, backgroundColor: "#fff" }}>
          No solutions attached yet. Add solutions from the "Saved Drafts" list above to build this LAP overview.
        </Typography>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {attachedSolutions.map((solution) => {
            const data = solution.combinedData || solution;
            const id = solution.id || solution.entryId;
            
            const title = getSafeText(data.title_en || data.title_english || data.title) || "Untitled Solution";
            const description = getSafeText(data.story || data.aims || data.aim) || "No description available.";
            const country = getSafeText(data.location_country);
            
            const rddWords = formatRddTags(getSafeText(data.rdd_name || data.rdd));

            const rddTags = formatRddTags(getSafeText(data.rdd_name || data.rdd));
            
            return (
              <Grid item xs={12} sm={6} md={4} key={id}>
                <Paper 
                  onClick={() => {
                      if (isLapPublished) {
                          navigate(`/solutions/rural-development/solutions-catalog/${id}`, {
                              state: {
                                  backPath: `/workspaces/projects/${projectId}/step-3`,
                                  backLabel: "Back to Project"
                              }
                          });
                      }
                  }}
                  sx={{ 
                    p: 2.5, height: "100%", display: "flex", flexDirection: "column", 
                    borderRadius: 3, position: "relative",
                    border: "1px solid #E4E7EC",
                    cursor: isLapPublished ? "pointer" : "default",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(35, 87, 107, 0.12)" }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "#23576B", display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: "#667085", mb: 2, flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {description}
                  </Typography>
                  
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mt: 2 }}>
                    
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: "70%" }}>
                      {country && (
                        <Chip label={country} size="small" sx={{ backgroundColor: "#F5F7FA", border: "1px solid #D0D5DD", color: "#344054", fontWeight: 500, fontSize: "0.7rem" }} />
                      )}
                      {rddTags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            backgroundColor: "#F5F7FA", 
                            border: "1px solid #D0D5DD", 
                            color: "#344054", 
                            fontWeight: 500, 
                            fontSize: "0.7rem",
                            maxWidth: "100%", // Ensures it respects the parent's width
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis" // Adds the ... if the RDD name is too long
                            }
                          }} 
                        />
                      ))}
                    </Box>

                    {/* Action Icons - Hidden entirely when LAP is published */}
                    {!isLapPublished && (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            onClick={(e) => { e.stopPropagation(); setViewDraft(solution); }} 
                            size="small" 
                            sx={{ color: "#23576B", backgroundColor: "#F4F7F9", "&:hover": { backgroundColor: "#E4E7EC" } }}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from LAP">
                          <IconButton 
                            onClick={(e) => { e.stopPropagation(); confirmRemove(id); }} 
                            size="small" 
                            sx={{ color: "#d32f2f", backgroundColor: "#fef2f2", "&:hover": { backgroundColor: "#fee2e2" } }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Publish Button - Hidden if Published */}
      {!isLapPublished && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={() => setOpenPublishDialog(true)}
            disabled={!lap?.id || attachedSolutions.length === 0 || publishing || isPublishingSequence}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: 2, px: 4, py: 1, backgroundColor: default_theme.palette.other.chatGreen, color: "#fff",
              "&:hover": { backgroundColor: default_theme.palette.other.chatGreenHover },
            }}
          >
            {/* Add isPublishingSequence here to keep it spinning: */}
            {publishing || isPublishingSequence ? (
              <><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> Publishing...</>
            ) : (
              "Publish LAP"
            )}
          </Button>
        </Box>
      )}

      {/* PUBLISH CONFIRMATION DIALOG */}
      <Dialog open={openPublishDialog} onClose={() => setOpenPublishDialog(false)}>
          <DialogTitle sx={{ fontWeight: "bold", color: "#23576B" }}>
              Publish Local Action Plan?
          </DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ color: "#475467" }}>
                  Are you sure you want to publish this Local Action Plan? 
                  <br /><br />
                  <strong>Warning:</strong> Once published, you will <strong>not</strong> be able to undo this action. You will no longer be able to edit, delete, or remove any solutions attached to this LAP.
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
              <Button onClick={() => setOpenPublishDialog(false)} sx={{ color: "#667085", fontWeight: 600 }}>
                  Cancel
              </Button>
              <Button 
                onClick={handleConfirmPublish} 
                variant="contained" 
                sx={{ backgroundColor: default_theme.palette.other.chatGreen, color: "#fff", fontWeight: 600, "&:hover": { backgroundColor: default_theme.palette.other.chatGreenHover } }}
              >
                  Yes, Publish
              </Button>
          </DialogActions>
      </Dialog>

      <DraftSolutionDetails
          open={Boolean(viewDraft)}
          draft={viewDraft}
          onClose={() => setViewDraft(null)}
          isPublished={true}
      />

      <Dialog 
          open={removeDialogOpen} 
          onClose={() => setRemoveDialogOpen(false)}
      >
          <DialogTitle sx={{ fontWeight: "bold", color: "#d32f2f" }}>
              Remove from Local Action Plan?
          </DialogTitle>
          <DialogContent>
              <DialogContentText>
                  Are you sure you want to remove this solution from the Local Action Plan? <br /><br />
                  <strong>Note:</strong> This will not delete the solution. It will remain saved in your Drafts above.
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setRemoveDialogOpen(false)} sx={{ color: "#667085", fontWeight: 600 }}>
                  Cancel
              </Button>
              <Button onClick={executeRemove} variant="contained" color="error" sx={{ fontWeight: 600 }}>
                  Remove
              </Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocalActionPlanSection;